/**
 * UIProvider - UI layer provider bundle
 * 
 * Provides UI-related providers that don't require ActrProvider.
 * Should be used inside CoreProvider.
 * 
 * CHANGE HISTORY:
 * - 2025-11-15 danielbeer: Initial creation - standardized provider orchestration
 */

import React, { ReactNode } from 'react';
import { InterfacrProvider, ProfylrProvider, AggregatrProvider, CachrProvider, PanelrProvider } from '@opcentral/dependr';
import { PromtrProvider } from '@opcentral/promptr';
import { DraggrProvider } from '@opcentral/draggr';

export interface UIProviderProps {
  children: ReactNode;
  
  // Feature toggles
  enableInterfacr?: boolean;
  enableProfiles?: boolean;
  enableAggregation?: boolean;
  enableCaching?: boolean;
  enableDialogs?: boolean;
  enableDragDrop?: boolean;
  enablePanels?: boolean;
  
  // Cachr configuration
  cachrPrefix?: string;
  
  // Profylr configuration
  profylrConfig?: any;
  
  // Panelr configuration
  panelrNamespace?: string;
}

/**
 * UIProvider - Provides UI layer
 * 
 * Hierarchy:
 * InterfacrProvider (UI context)
 *   └─ ProfylrProvider (profiles)
 *       └─ AggregatrProvider (data aggregation)
 *           └─ CachrProvider (caching)
 *               └─ PromtrProvider (dialogs/prompts)
 *                   └─ DraggrProvider (drag & drop)
 *                       └─ PanelrProvider (panels/sidebars)
 * 
 * These providers are standalone or have optional Actr integration.
 */
export function UIProvider({
  children,
  enableInterfacr = true,
  enableProfiles = true,
  enableAggregation = true,
  enableCaching = true,
  enableDialogs = true,
  enableDragDrop = true,
  enablePanels = true,
  cachrPrefix = 'app_',
  profylrConfig = {},
  panelrNamespace = 'app',
}: UIProviderProps) {
  
  let content = children;
  
  // Wrap in reverse order (innermost first)
  if (enablePanels) {
    content = <PanelrProvider namespace={panelrNamespace}>{content}</PanelrProvider>;
  }
  
  if (enableDragDrop) {
    content = <DraggrProvider>{content}</DraggrProvider>;
  }
  
  if (enableDialogs) {
    content = <PromtrProvider>{content}</PromtrProvider>;
  }
  
  if (enableCaching) {
    content = <CachrProvider options={{ prefix: cachrPrefix }}>{content}</CachrProvider>;
  }
  
  if (enableAggregation) {
    content = <AggregatrProvider>{content}</AggregatrProvider>;
  }
  
  if (enableProfiles) {
    content = <ProfylrProvider {...profylrConfig}>{content}</ProfylrProvider>;
  }
  
  if (enableInterfacr) {
    content = <InterfacrProvider>{content}</InterfacrProvider>;
  }
  
  return <>{content}</>;
}

export default UIProvider;
